import { API_TYPES, post } from "@src/appV2/api";
import { useCbhQueryClient } from "@src/appV2/api/queryClient";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics/log";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { useLocalStorage } from "@src/appV2/lib/utils";
import type { HiringFeedsResponse } from "@src/appV2/redesign/HiringFeed/api/hiringFeed.schema";
import { fetchHiringFeedApiUrl } from "@src/appV2/redesign/HiringFeed/api/usePaginatedHiringFeed";
import { fetchPlacementsApiUrl } from "@src/appV2/redesign/Placements/api/useFetchPaginatedPlacements";
import { PLACEMENT_PATHS } from "@src/appV2/redesign/Placements/paths";
import type {
  GetPlacementsResponse,
  PlacementResponse,
} from "@src/appV2/redesign/Placements/types/fetchPlacements.schema";
import { showApplicationsStorageKey } from "@src/appV2/redesign/Placements/utils";
import {
  type InfiniteData,
  type QueryClient,
  useMutation,
  type UseMutationOptions,
} from "@tanstack/react-query";
import { useHistory } from "react-router-dom";
import { z } from "zod";

import { FavoritePlacementSuccessMessage } from "../components/FavoritePlacementSuccessMessage";
import { workerFavoritePlacementDataSchema } from "../types/workerFavoritePlacement.schema";
import { getPlacementUrl } from "./useGetPlacement";

// Local schemas - specific to this hook
const createWorkerFavoritePlacementRequestSchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.workerFavoritePlacement),
    relationships: z.object({
      placement: z.object({
        data: z.object({
          type: z.literal(API_TYPES.placement),
          id: z.string(),
        }),
      }),
    }),
  }),
});

const createWorkerFavoritePlacementResponseSchema = z.object({
  data: workerFavoritePlacementDataSchema,
});

// FIXME: Automatically disabled during ESLint config update
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const createWorkerFavoritePlacementParamsSchema = z.object({
  workerId: z.string(),
  placementId: z.string(),
  source: z.string(),
});

type CreateWorkerFavoritePlacementParams = z.infer<
  typeof createWorkerFavoritePlacementParamsSchema
>;

type CreateWorkerFavoritePlacementResponse = z.infer<
  typeof createWorkerFavoritePlacementResponseSchema
>;

export function useCreateWorkerFavoritePlacement(
  options: UseMutationOptions<
    CreateWorkerFavoritePlacementResponse,
    string,
    CreateWorkerFavoritePlacementParams
  > = {}
) {
  const queryClient = useCbhQueryClient();
  const { showSuccessToast } = useToast();
  const [, setShowApplications] = useLocalStorage<boolean>(showApplicationsStorageKey, true);
  const history = useHistory();

  return useMutation({
    mutationFn: async (params: CreateWorkerFavoritePlacementParams) => {
      const { workerId, placementId } = params;

      await cancelPlacementFetchingQueries(queryClient, workerId, placementId);

      optimisticallySetPlacementFavoritedState(queryClient, workerId, placementId, true);

      const response = await post({
        url: `${environmentConfig.REACT_APP_BASE_API_URL}/workers/${workerId}/favorite-placements`,
        data: {
          data: {
            type: API_TYPES.workerFavoritePlacement,
            relationships: {
              placement: {
                data: {
                  type: API_TYPES.placement,
                  id: placementId,
                },
              },
            },
          },
        },
        requestSchema: createWorkerFavoritePlacementRequestSchema,
        responseSchema: createWorkerFavoritePlacementResponseSchema,
      });
      return response.data;
    },
    onSuccess: async (_data, variables, _context) => {
      const { workerId, placementId, source } = variables ?? {};

      logEvent(APP_V2_APP_EVENTS.PLACEMENT_FAVORITED, {
        placementId,
        workerId,
        source,
      });

      await invalidatePlacementFetchingQueries(queryClient, workerId, placementId);

      // Show success toast with link
      showSuccessToast(
        <FavoritePlacementSuccessMessage
          onClick={() => {
            setShowApplications(true);

            history.push(PLACEMENT_PATHS.ROOT);
          }}
        />
      );
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.PLACEMENT_FAVORITED_FAILURE,
    },
    ...options,
  });
}

export async function invalidatePlacementFetchingQueries(
  queryClient: QueryClient,
  workerId: string,
  placementId: string
) {
  await queryClient.invalidateQueries({
    queryKey: [
      `${environmentConfig.REACT_APP_BASE_API_URL}/workers/${workerId}/favorite-placements`,
    ],
  });
  await queryClient.invalidateQueries({ queryKey: [fetchHiringFeedApiUrl(workerId)] });
  await queryClient.invalidateQueries({ queryKey: [getPlacementUrl(placementId, workerId)] });
  await queryClient.invalidateQueries({ queryKey: [fetchPlacementsApiUrl(workerId)] });
}

export async function cancelPlacementFetchingQueries(
  queryClient: QueryClient,
  workerId: string,
  placementId: string
) {
  await queryClient.cancelQueries({
    queryKey: [
      `${environmentConfig.REACT_APP_BASE_API_URL}/workers/${workerId}/favorite-placements`,
    ],
  });
  await queryClient.cancelQueries({ queryKey: [fetchHiringFeedApiUrl(workerId)] });
  await queryClient.cancelQueries({ queryKey: [fetchPlacementsApiUrl(workerId)] });
  await queryClient.cancelQueries({ queryKey: [getPlacementUrl(placementId, workerId)] });
}

export function optimisticallySetPlacementFavoritedState(
  queryClient: QueryClient,
  workerId: string,
  placementId: string,
  newFavoritedState: boolean
) {
  // update the hiring-feed data
  queryClient.setQueriesData<InfiniteData<HiringFeedsResponse>>(
    [fetchHiringFeedApiUrl(workerId)],
    (oldData) => updatePaginatedPlacementsFavoriteState(oldData, placementId, newFavoritedState)
  );

  queryClient.setQueriesData<InfiniteData<GetPlacementsResponse>>(
    [fetchPlacementsApiUrl(workerId)],
    (oldData) => updatePaginatedPlacementsFavoriteState(oldData, placementId, newFavoritedState)
  );

  queryClient.setQueriesData<PlacementResponse>(
    [getPlacementUrl(placementId, workerId)],
    (oldData) => {
      if (!oldData) {
        return oldData;
      }

      return {
        ...oldData,
        data: {
          ...oldData.data,
          meta: {
            ...oldData.data.meta,
            isFavorited: newFavoritedState,
          },
        },
      };
    }
  );
}

function updatePaginatedPlacementsFavoriteState<
  T extends { data: Array<{ id: string; meta: { isFavorited?: boolean | undefined } }> }
>(
  oldData: InfiniteData<T> | undefined,
  placementId: string,
  favoritedState: boolean
): InfiniteData<T> | undefined {
  if (!oldData) {
    return oldData;
  }

  const newPages = oldData.pages.map((page) => {
    if (!page) {
      return page;
    }

    const newData = page.data.map((item) => {
      if (item.id === placementId) {
        return {
          ...item,
          meta: {
            ...item.meta,
            isFavorited: favoritedState,
          },
        };
      }

      return item;
    });

    return {
      ...page,
      data: newData,
    };
  });

  return { ...oldData, pages: newPages };
}
